import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { OPERATORS } from "@/lib/branding";
import { Loader2, Music2, Upload, Trash2, Play, Plus, X, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export const listPacks = () => api.get("/sounds/packs").then((r) => r.data);
export const listPhases = () => api.get("/sounds/phases").then((r) => r.data);

export default function Sounds() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [operator, setOperator] = useState("AC");
  const [description, setDescription] = useState("");

  const { data: packs = [], isLoading } = useQuery({ queryKey: ["sound-packs"], queryFn: listPacks });
  const { data: phasesData } = useQuery({ queryKey: ["sound-phases"], queryFn: listPhases });
  const phases = phasesData?.phases || [];

  const createPack = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append("name", name);
      fd.append("operator", operator);
      fd.append("description", description);
      return api.post("/sounds/packs", fd).then((r) => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sound-packs"] });
      setCreating(false);
      setName("");
      setDescription("");
      toast.success("Soundpack created");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Create failed"),
  });

  const deletePack = useMutation({
    mutationFn: (id) => api.delete(`/sounds/packs/${id}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sound-packs"] });
      toast.success("Deleted");
    },
  });

  return (
    <div className="p-8 max-w-6xl" data-testid="sounds-page">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="overline mb-1">Soundpacks</div>
          <h1 className="text-3xl font-bold tracking-tight">Cabin & flight-deck audio</h1>
          <div className="acbar" />
          <p className="text-sm text-muted-foreground mt-4 max-w-2xl">
            Upload MP3/WAV/OGG sound files against flight phases (boarding, taxi,
            takeoff, cruise, landing, arrival, …). The player will automatically
            play the correct clip when your live flight enters that phase based
            on the operator you selected at init (Mainline / Express / Leisure / …).
          </p>
        </div>
        {user?.is_admin && (
          <button
            onClick={() => setCreating(true)}
            data-testid="sounds-new-pack"
            className="bg-primary text-primary-foreground text-sm font-bold uppercase tracking-widest px-4 py-2.5 hover:brightness-110 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> New pack
          </button>
        )}
      </div>

      {!user?.is_admin && (
        <div className="card-flat px-5 py-4 mb-6 flex items-start gap-3" data-testid="sounds-viewer-notice">
          <ShieldAlert className="w-4 h-4 text-primary mt-0.5" />
          <div className="text-xs text-muted-foreground">
            You have viewer access. Only ops staff can upload or edit soundpacks. Pilots
            still hear the sounds automatically during their flights.
          </div>
        </div>
      )}

      {/* Create Pack modal */}
      {creating && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="card-flat p-6 w-full max-w-md" data-testid="sounds-new-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Create soundpack</h3>
              <button onClick={() => setCreating(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>
            <label className="block mb-3">
              <span className="overline block mb-1.5">Name</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="AC – Boeing 737 default"
                data-testid="sounds-new-name"
                className="w-full bg-background border border-border px-3 py-2 text-sm text-foreground"
              />
            </label>
            <label className="block mb-3">
              <span className="overline block mb-1.5">Operator</span>
              <select
                value={operator}
                onChange={(e) => setOperator(e.target.value)}
                data-testid="sounds-new-operator"
                className="w-full bg-background border border-border px-3 py-2 text-sm font-mono text-foreground"
              >
                {OPERATORS.map((o) => (
                  <option key={o.code} value={o.code}>{`${o.code} · ${o.name}`}</option>
                ))}
              </select>
            </label>
            <label className="block mb-6">
              <span className="overline block mb-1.5">Description (optional)</span>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                data-testid="sounds-new-desc"
                rows={3}
                className="w-full bg-background border border-border px-3 py-2 text-sm text-foreground"
              />
            </label>
            <button
              onClick={() => createPack.mutate()}
              disabled={!name || createPack.isPending}
              data-testid="sounds-new-submit"
              className="w-full bg-primary text-primary-foreground text-sm font-bold uppercase tracking-widest py-2.5 disabled:opacity-60"
            >
              {createPack.isPending ? "Creating…" : "Create"}
            </button>
          </div>
        </div>
      )}

      <div className="grid gap-4">
        {isLoading ? (
          <div className="card-flat p-6 text-sm text-muted-foreground">Loading…</div>
        ) : packs.length === 0 ? (
          <div className="card-flat p-10 text-center">
            <Music2 className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
            <div className="text-sm text-muted-foreground">
              No soundpacks yet. {user?.is_admin ? "Create one to start uploading audio." : "Ask an admin to add one."}
            </div>
          </div>
        ) : (
          packs.map((p) => (
            <PackCard
              key={p.id}
              pack={p}
              phases={phases}
              isAdmin={user?.is_admin}
              onDelete={() => {
                if (window.confirm(`Delete soundpack "${p.name}"?`)) deletePack.mutate(p.id);
              }}
            />
          ))
        )}
      </div>
    </div>
  );
}

function PackCard({ pack, phases, isAdmin, onDelete }) {
  const qc = useQueryClient();
  const fileRef = useRef({});
  const [uploadingPhase, setUploadingPhase] = useState(null);
  // API_BASE, not the raw env var. Unset — which is the normal same-origin
  // deployment — `process.env.REACT_APP_BACKEND_URL` is undefined and this
  // built "undefined/api/sounds/file/x.mp3", so no clip has ever played in
  // the browser build. api.js normalises this to "" for exactly this reason;
  // rebuilding the base by hand walked straight back into it.
  const backendBase = API_BASE;

  const uploadFile = async (phase, file) => {
    setUploadingPhase(phase);
    try {
      const fd = new FormData();
      fd.append("phase", phase);
      fd.append("file", file);
      await api.post(`/sounds/packs/${pack.id}/upload`, fd);
      qc.invalidateQueries({ queryKey: ["sound-packs"] });
      toast.success(`Uploaded ${phase}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingPhase(null);
    }
  };

  const removePhase = async (phase) => {
    if (!window.confirm(`Remove ${phase} sound?`)) return;
    await api.delete(`/sounds/packs/${pack.id}/phase/${phase}`);
    qc.invalidateQueries({ queryKey: ["sound-packs"] });
  };

  return (
    <div className="card-flat" data-testid={`sound-pack-${pack.id}`}>
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-mono font-bold bg-primary text-primary-foreground px-2 py-0.5">
              {pack.operator}
            </span>
            <h3 className="text-base font-bold">{pack.name}</h3>
          </div>
          {pack.description && (
            <p className="text-xs text-muted-foreground mt-1">{pack.description}</p>
          )}
        </div>
        {isAdmin && (
          <button
            onClick={onDelete}
            data-testid={`sound-pack-delete-${pack.id}`}
            className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete pack
          </button>
        )}
      </div>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {phases.map((phase) => {
          const meta = pack.sounds?.[phase];
          const url = meta ? `${backendBase}/api/sounds/file/${meta.filename}` : null;
          return (
            <div key={phase} className="border border-border p-3 flex items-center justify-between gap-2" data-testid={`sound-phase-${pack.id}-${phase}`}>
              <div className="min-w-0 flex-1">
                <div className="overline">{phase.replace(/_/g, " ")}</div>
                {meta ? (
                  <>
                    <div className="text-xs font-mono text-muted-foreground truncate">
                      {meta.original_name || meta.filename}
                    </div>
                    <audio src={url} controls className="w-full h-8 mt-1" />
                  </>
                ) : (
                  <div className="text-xs text-muted-foreground italic mt-1">No file</div>
                )}
              </div>
              {isAdmin && (
                <div className="flex flex-col gap-1">
                  <input
                    ref={(el) => (fileRef.current[phase] = el)}
                    type="file"
                    accept="audio/*"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadFile(phase, f);
                      e.target.value = "";
                    }}
                  />
                  <button
                    onClick={() => fileRef.current[phase]?.click()}
                    disabled={uploadingPhase === phase}
                    data-testid={`sound-upload-${pack.id}-${phase}`}
                    className="text-xs bg-secondary hover:bg-primary hover:text-primary-foreground px-2 py-1 flex items-center gap-1"
                  >
                    {uploadingPhase === phase ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                  </button>
                  {meta && (
                    <button
                      onClick={() => removePhase(phase)}
                      className="text-xs text-muted-foreground hover:text-primary px-2 py-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
